import { defineStore } from 'pinia'
import { ref } from 'vue'

const AUTO_DISMISS_MS = 4000

/**
 * Store global de toasts.
 *
 * Se usa desde componentes (`useToastStore()` en setup) y desde el interceptor
 * de `services/api.js` — allí se importa de forma perezosa DENTRO de la función
 * del interceptor, porque api.js se evalúa antes de que Pinia esté activo.
 */
export const useToastStore = defineStore('toast', () => {
  const items = ref([])

  let _nextId = 1
  const _timers = new Map()

  const remove = (id) => {
    const timer = _timers.get(id)
    if (timer) {
      clearTimeout(timer)
      _timers.delete(id)
    }
    items.value = items.value.filter(t => t.id !== id)
  }

  const push = (type, message) => {
    const text = typeof message === 'string' ? message : String(message ?? '')
    if (!text) return null
    // Dedup (H4, revisión 2026-07-18): el interceptor global de api.js y el
    // catch del componente pueden reportar el mismo error → un solo toast
    // visible por (tipo, mensaje).
    const dup = items.value.find(t => t.type === type && t.message === text)
    if (dup) return dup.id
    const id = _nextId++
    items.value.push({ id, type, message: text })
    _timers.set(id, setTimeout(() => remove(id), AUTO_DISMISS_MS))
    return id
  }

  const success = (message) => push('success', message)
  const error = (message) => push('error', message)
  const info = (message) => push('info', message)

  const clear = () => {
    for (const timer of _timers.values()) clearTimeout(timer)
    _timers.clear()
    items.value = []
  }

  return { items, success, error, info, remove, clear }
})
